import AddIcon from "@mui/icons-material/Add";
import { Box, IconButton, Typography } from "@mui/material";
import React from "react";
import { API_HEADERS } from '../../../../constants/apiHeaders';
import api from "../../../../services/httpClient";
import { SETTINGS_ENDPOINTS } from '../../../../services/endpoints/settings.endpoints';
import { SYSTEM_ENDPOINTS } from '../../../../services/endpoints/system.endpoints';
import { AsyncAutocomplete, Option } from "../../../common/AsyncAutocomplete";
import CloseIcon from "../../../common/icons/CloseIcon";
import SelectField from "../../../common/SelectField";
import TextField from "../../../common/TextField";
import type { QualificationEntry, SkillEntry, TechnologyEntry } from '../../../../services/types/vacancy.types';

interface VacancySkillCardProps {
  type: 'add' | 'edit' | 'view';
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
  qualifications: QualificationEntry[];
  primarySkills: SkillEntry[];
  secondarySkills: SkillEntry[];
  primaryTechnologies: TechnologyEntry[];
  secondaryTechnologies: TechnologyEntry[];
  setQualifications: (qualifications: QualificationEntry[]) => void;
  setPrimarySkills: (primarySkills: SkillEntry[]) => void;
  setSecondarySkills: (secondarySkills: SkillEntry[]) => void;
  setPrimaryTechnologies: (primaryTechnologies: TechnologyEntry[]) => void;
  setSecondaryTechnologies: (secondaryTechnologies: TechnologyEntry[]) => void;
  onError?: (message?: string) => void;
}
const VacancySkillCard: React.FC<VacancySkillCardProps> = ({
  type,
  errors,
  setErrors,
  qualifications,
  primarySkills,
  secondarySkills,
  primaryTechnologies,
  secondaryTechnologies,
  setQualifications,
  setPrimarySkills,
  setSecondarySkills,
  setPrimaryTechnologies,
  setSecondaryTechnologies,
  onError,
}) => {
  const addEntry = (
    section:
      | 'qualifications'
      | 'primarySkills'
      | 'secondarySkills'
      | 'primaryTechnologies'
      | 'secondaryTechnologies',
  ) => {
    const newId = Date.now().toString();

    switch (section) {
      case 'qualifications':
        setQualifications([
          ...qualifications,
          { id: newId, qualification: null, type: "" },
        ]);
        break;
      case "primarySkills":
        if (primarySkills.length < 2) {
          setPrimarySkills([
            ...primarySkills,
            { id: newId, skill: null, years: "" },
          ]);
        }
        break;
      case 'secondarySkills':
        setSecondarySkills([
          ...secondarySkills,
          { id: newId, skill: null, years: "" },
        ]);
        break;
      case "primaryTechnologies":
        if (primaryTechnologies.length < 2) {
          setPrimaryTechnologies([
            ...primaryTechnologies,
            { id: newId, technology: null, years: "" },
          ]);
        }
        break;
      case 'secondaryTechnologies':
        setSecondaryTechnologies([
          ...secondaryTechnologies,
          { id: newId, technology: null, years: "" },
        ]);
        break;
    }
  };

  const removeEntry = (
    section:
      | 'qualifications'
      | 'primarySkills'
      | 'secondarySkills'
      | 'primaryTechnologies'
      | 'secondaryTechnologies',
    id: string,
  ) => {
    switch (section) {
      case 'qualifications':
        setQualifications(qualifications.filter((item) => item.id !== id));
        break;
      case 'primarySkills':
        setPrimarySkills(primarySkills.filter((item) => item.id !== id));
        break;
      case 'secondarySkills':
        setSecondarySkills(secondarySkills.filter((item) => item.id !== id));
        break;
      case 'primaryTechnologies':
        setPrimaryTechnologies(
          primaryTechnologies.filter((item) => item.id !== id),
        );
        break;
      case 'secondaryTechnologies':
        setSecondaryTechnologies(
          secondaryTechnologies.filter((item) => item.id !== id),
        );
        break;
    }
  };

  const handleLoadQualifications = async (
    query: string,
    otherQuery?: string,
  ) => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SYSTEM_ENDPOINTS.qualifications,
      {
        headers: { ...API_HEADERS.SETTINGS_QUALIFICATIONS_VACANCY_ADD },
      },
    );
    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadSkills = async (query: string) => {
    if (!query.trim() || query.length < 3) {
      return [];
    }

    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SETTINGS_ENDPOINTS.skills,
      {
        headers: { ...API_HEADERS.SETTINGS_SKILLS_VACANCY_ADD },
      },
    );
    return (response.data.data ?? []).map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleCreateSkill = async (name: string): Promise<Option> => {
    const skillName = name.trim();
    if (!skillName) throw new Error('Skill name is empty');

    try {
      const response = await api.post<any>(
        SETTINGS_ENDPOINTS.skills,
        { name: skillName },
        {
          headers: { ...API_HEADERS.VACANCY_SKILL_CREATE_SKILL },
        }
      );

      const payload = response.data?.data ?? response.data;
      if (!payload?.id || !payload?.name) throw new Error('Invalid create-skill response');

      return { value: String(payload.id), label: String(payload.name) };
    } catch (e) {
      onError?.('Failed to create skill. Please try again.');
      throw e;
    }
  };

  const handleLoadTechnologies = async (query: string) => {
    if (!query.trim() || query.length < 3) {
      return [];
    }

    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SETTINGS_ENDPOINTS.technologies,
      {
        headers: { ...API_HEADERS.VACANCY_SKILL_TECHNOLOGIES_GET },
      },
    );
    return (response.data.data ?? []).map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleQualificationChange = (
    id: string,
    field: keyof QualificationEntry,
    value: string | Option | null,
  ) => {
    setQualifications(
      qualifications.map((qual) => {
        if (qual.id === id) {
          return {
            ...qual,
            [field]: value,
          };
        }
        return qual;
      }),
    );
    setErrors({ ...errors, qualifications: "" });
  };

   const handleCreateTechnology = async (name: string): Promise<Option> => {
    const technologyName = name.trim();
    if (!technologyName) throw new Error('Technology name is empty');

    try {
      const response = await api.post<any>(
        SETTINGS_ENDPOINTS.technologies,
        { name: technologyName },
        {
          headers: { ...API_HEADERS.VACANCY_SKILL_CREATE_TECHNOLOGY },
        }
      );

      const payload = response.data?.data ?? response.data;
      if (!payload?.id || !payload?.name) throw new Error('Invalid create-technology response');

      return { value: String(payload.id), label: String(payload.name) };
    } catch (e) {
      onError?.('Failed to create technology. Please try again.');
      throw e;
    }
  };

  const clampYearsValue = (value: string | Option | null): string | Option | null => {
    if (typeof value !== "string" || value === "") return value;
    const n = Number(value);
    if (Number.isNaN(n) || n >= 0) return value;
    return "";
  };

  const handleSkillChange = (
    id: string,
    field: keyof SkillEntry,
    value: string | Option | null,
    section: "primary" | "secondary",
  ) => {
    const nextValue = field === "years" ? clampYearsValue(value) : value;
    if (section === "primary") {
      setPrimarySkills(
        primarySkills.map((skill) => {
          if (skill.id === id) {
            return {
              ...skill,
              [field]: nextValue,
            };
          }
          return skill;
        }),
      );
      setErrors({ ...errors, primarySkills: "" });
    } else {
      setSecondarySkills(
        secondarySkills.map((skill) => {
          if (skill.id === id) {
            return {
              ...skill,
              [field]: nextValue,
            };
          }
          return skill;
        }),
      );
    }
  };

  const handleTechnologyChange = (
    id: string,
    field: keyof TechnologyEntry,
    value: string | Option | null,
    section: 'primary' | 'secondary',
  ) => {
    const nextValue = field === "years" ? clampYearsValue(value) : value;
    if (section === 'primary') {
      setPrimaryTechnologies(
        primaryTechnologies.map((tech) => {
          if (tech.id === id) {
            return {
              ...tech,
              [field]: nextValue,
            };
          }
          return tech;
        }),
      );
      setErrors({ ...errors, primaryTechnologies: "" });
    } else {
      setSecondaryTechnologies(
        secondaryTechnologies.map((tech) => {
          if (tech.id === id) {
            return {
              ...tech,
              [field]: nextValue,
            };
          }
          return tech;
        }),
      );
    }
  };

  return (
    <Box className="edit-section">
      {/* Qualifications */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          width: '900px',
        }}
      >
        <Typography className="edit-section-title">Qualifications*</Typography>
        {type !== 'view' && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry("qualifications")}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {qualifications.map((qual, index) => (
        <Box
          key={qual.id}
          className="edit-sub-section"
          data-testid="qualification-row"
          sx={{ width: '900px', mb: 2, position: 'relative' }}
        >
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <SelectField
                label="Type"
                width="360px"
                options={[
                  { value: "Degree", label: "Degree" },
                  { value: "Diploma", label: "Diploma" },
                  { value: "Certificate", label: "Certificate" },
                ]}
                value={qual.type}
                onChange={(e) =>
                  handleQualificationChange(
                    qual.id,
                    'type',
                    e.target.value as string,
                  )
                }
                name={`qualifications[${index}].type`}
              />
            </Box>
            <Box>
              <AsyncAutocomplete
                label="Qualification"
                width="360px"
                loadOptions={(query, otherQuery) =>
                  handleLoadQualifications(query, otherQuery)
                }
                otherQuery={qual.type}
                value={qual.qualification}
                onChange={(value) =>
                  handleQualificationChange(
                    qual.id,
                    'qualification',
                    value as Option,
                  )
                }
              />
            </Box>
            {type !== "view" && qualifications.length > 1 && (
              <IconButton
                onClick={() => removeEntry("qualifications", qual.id)}
                className="edit-section-close-button"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
      {errors.qualifications && (
        <Typography
          sx={{ color: "#E90000", fontSize: "0.75rem", mt: 1, mb: 2 }}
        >
          {errors.qualifications}
        </Typography>
      )}

      {/* Primary Skills */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          mt: 4,
          width: '900px',
        }}
      >
        <Typography className="edit-section-title">
          Primary Skills (2 Skills Maximum)*
        </Typography>
        {type !== "view" && (
          <IconButton
            className="edit-section-add-button"
            disabled={primarySkills.length >= 2}
            onClick={() => addEntry("primarySkills")}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {primarySkills.map((skill, index) => (
        <Box
          key={skill.id}
          className="edit-sub-section"
          sx={{ width: '900px', mb: 2, position: 'relative' }}
        >
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Skill"
                width="360px"
                loadOptions={handleLoadSkills}
                loadOptionsOnOpen={false}
                value={skill.skill}
                onChange={(value) => handleSkillChange(skill.id, 'skill', value as Option, 'primary')}
                allowCreate={type !== 'view'}
                minCreateLength={3}
                onCreate={handleCreateSkill}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={skill.years}
                onChange={(e) =>
                  handleSkillChange(
                    skill.id,
                    'years',
                    e.target.value as string,
                    'primary',
                  )
                }
                name={`primarySkills[${index}].years`}
              />
            </Box>
            {type !== "view" && primarySkills.length > 1 && (
              <IconButton
                onClick={() => removeEntry("primarySkills", skill.id)}
                className="edit-section-close-button"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
      {errors.primarySkills && (
        <Typography
          sx={{ color: "#E90000", fontSize: "0.75rem", mt: 1, mb: 2 }}
        >
          {errors.primarySkills}
        </Typography>
      )}

      {/* Secondary Skills */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          mt: 4,
          width: '900px',
        }}
      >
        <Typography className="edit-section-title">Secondary Skills</Typography>
        {type !== 'view' && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry("secondarySkills")}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {secondarySkills.map((skill, index) => (
        <Box
          key={skill.id}
          className="edit-sub-section"
          sx={{ width: '900px', mb: 2, position: 'relative' }}
        >
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Skill"
                width="360px"
                loadOptions={handleLoadSkills}
                loadOptionsOnOpen={false}
                value={skill.skill}
                onChange={(value) => handleSkillChange(skill.id, 'skill', value as Option, 'secondary')}
                allowCreate={type !== 'view'}
                minCreateLength={3}
                onCreate={handleCreateSkill}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={skill.years}
                onChange={(e) =>
                  handleSkillChange(
                    skill.id,
                    'years',
                    e.target.value as string,
                    'secondary',
                  )
                }
                name={`secondarySkills[${index}].years`}
              />
            </Box>
            {type !== "view" && secondarySkills.length > 1 && (
              <IconButton
                onClick={() => removeEntry("secondarySkills", skill.id)}
                className="edit-section-close-button"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      {/* Primary Technologies */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          mt: 4,
          width: '900px',
        }}
      >
        <Typography className="edit-section-title">
          Primary Technologies (2 Technologies Maximum)*
        </Typography>
        {type !== "view" && (
          <IconButton
            className="edit-section-add-button"
            disabled={primaryTechnologies.length >= 2}
            onClick={() => addEntry("primaryTechnologies")}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {primaryTechnologies.map((tech, index) => (
        <Box
          key={tech.id}
          className="edit-sub-section"
          sx={{ width: '900px', mb: 2, position: 'relative' }}
        >
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Technology"
                width="360px"
                loadOptionsOnOpen={false}
                loadOptions={handleLoadTechnologies}
                value={tech.technology}
                onChange={(value) => handleTechnologyChange(tech.id, 'technology', value as Option, 'primary')}
                allowCreate={type !== 'view'}
                minCreateLength={3}
                onCreate={handleCreateTechnology}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={tech.years}
                onChange={(e) =>
                  handleTechnologyChange(
                    tech.id,
                    'years',
                    e.target.value as string,
                    'primary',
                  )
                }
                name={`primaryTechnologies[${index}].years`}
              />
            </Box>
            {type !== "view" && primaryTechnologies.length > 1 && (
              <IconButton
                onClick={() => removeEntry("primaryTechnologies", tech.id)}
                className="edit-section-close-button"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
      {errors.primaryTechnologies && (
        <Typography
          sx={{ color: "#E90000", fontSize: "0.75rem", mt: 1, mb: 2 }}
        >
          {errors.primaryTechnologies}
        </Typography>
      )}

      {/* Secondary Technologies */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          mt: 4,
          width: '900px',
        }}
      >
        <Typography className="edit-section-title">
          Secondary Technologies
        </Typography>
        {type !== "view" && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry("secondaryTechnologies")}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {secondaryTechnologies.map((tech, index) => (
        <Box
          key={tech.id}
          className="edit-sub-section"
          sx={{ width: '900px', mb: 2, position: 'relative' }}
        >
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Technology"
                width="360px"
                loadOptionsOnOpen={false}
                loadOptions={handleLoadTechnologies}
                value={tech.technology}
                onChange={(value) => handleTechnologyChange(tech.id, 'technology', value as Option, 'secondary')}
                allowCreate={type !== 'view'}
                minCreateLength={3}
                onCreate={handleCreateTechnology}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={tech.years}
                onChange={(e) =>
                  handleTechnologyChange(
                    tech.id,
                    'years',
                    e.target.value as string,
                    'secondary',
                  )
                }
                name={`secondaryTechnologies[${index}].years`}
              />
            </Box>
            {type !== "view" && secondaryTechnologies.length > 1 && (
              <IconButton
                onClick={() => removeEntry("secondaryTechnologies", tech.id)}
                className="edit-section-close-button"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default VacancySkillCard;
